import { Sparkles } from "lucide-react";

/**
 * Sentence scaffolds — pre-built frames for kids who can match a noun to a
 * frame but can't yet build sentences. Tapping a scaffold drops 1+ tiles into
 * the sentence strip (e.g. "I want ___" places "I" + "want" then waits for
 * the child's next tile).
 */

const SCAFFOLDS_BY_LANG = {
  en: [
    { id: "i_want", label: "I want…", words: ["I", "want"] },
    { id: "i_feel", label: "I feel…", words: ["I", "feel"] },
    { id: "i_need", label: "I need…", words: ["I", "need"] },
    { id: "i_like", label: "I like…", words: ["I", "like"] },
    { id: "i_dont", label: "I don't…", words: ["I", "don't"] },
    { id: "more_please", label: "More ___ please", words: ["more"] },
  ],
  es: [
    { id: "i_want", label: "Quiero…", words: ["yo", "quiero"] },
    { id: "i_feel", label: "Me siento…", words: ["yo"] },
    { id: "i_need", label: "Necesito…", words: ["yo", "necesito"] },
    { id: "i_like", label: "Me gusta…", words: ["Me gusta"] },
    { id: "i_dont", label: "No…", words: ["no"] },
    { id: "more_please", label: "Más ___ por favor", words: ["más"] },
  ],
  fr: [
    { id: "i_want", label: "Je veux…", words: ["je", "veux"] },
    { id: "i_feel", label: "Je suis…", words: ["je"] },
    { id: "i_need", label: "J'ai besoin…", words: ["je", "besoin"] },
    { id: "i_like", label: "J'aime…", words: ["J'aime ça"] },
    { id: "i_dont", label: "Je n'aime pas…", words: ["non"] },
    { id: "more_please", label: "Plus ___ s'il te plaît", words: ["plus"] },
  ],
};

export default function SentenceScaffolds({ lang = "en", tilesByLabel, onPickFrame, "data-testid": testId }) {
  const items = SCAFFOLDS_BY_LANG[lang] || SCAFFOLDS_BY_LANG.en;

  const handle = (frame) => {
    // Resolve each frame word to an actual tile (case-insensitive). Skip
    // missing words gracefully so the frame still kicks off the sentence.
    const tiles = [];
    for (const w of frame.words) {
      const tile = tilesByLabel.get(w.toLowerCase());
      if (tile) tiles.push(tile);
    }
    if (tiles.length === 0) {
      // Fall back to a synthetic strip-item so the kid still sees the start.
      tiles.push({
        id: `frame-${frame.id}-${Date.now()}`,
        label: frame.label.replace(/…|\.\.\./, "").trim(),
        emoji: "🧩",
        color: "#FBEFD9",
        speak_text: frame.label.replace(/…|\.\.\./, "").trim(),
      });
    }
    onPickFrame?.(tiles);
  };

  return (
    <div
      className="flex items-center gap-2 mt-3 overflow-x-auto tile-scroll pb-1"
      data-testid={testId || "sentence-scaffolds"}
    >
      <span className="display text-sm font-semibold uppercase tracking-[0.2em] text-[color:var(--ink-soft)] flex items-center gap-1 shrink-0">
        <Sparkles size={14} className="text-[color:var(--gold)]" />
        Frames
      </span>
      <div className="flex gap-2">
        {items.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => handle(f)}
            className="px-3 py-2 rounded-2xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] shadow-[3px_3px_0_0_var(--ink)] flex items-center gap-2 hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform shrink-0"
            data-testid={`scaffold-${f.id}`}
          >
            <span className="display font-semibold text-[color:var(--ink)] whitespace-nowrap">
              {f.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
